// src/components/Anaco/FloatingItemsBackground.js
// UPDATED: Changed SVG import method for CRA v5 compatibility.
// Background animation with floating dots and ANACO logos.
// Uses Green/White/Grey Theme associated with ANACO.
// Current time: Friday, May 2, 2025 at 7:50 PM CST. San José, Costa Rica.

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
// import { FiBox } from 'react-icons/fi'; // No longer needed for placeholder

// --- SVG Import (CRA v5+ Method) ---
// Import the SVG file path directly
import anacoLogoUrl from "../../images/anaco.svg"; // Adjust path if needed

// --- Theme Colors (Using increased contrast version) ---
const colors = {
  gradientFrom: "from-slate-50",
  gradientTo: "to-emerald-50",
  dotColor: "rgba(22, 163, 74, 0.3)",
  // Removed logo placeholder colors as the SVG has its own fill
};

// --- Configuration ---
const NUM_DOTS = 75;
const NUM_LOGOS = 10;
const MIN_DOT_SIZE = 3;
const MAX_DOT_SIZE = 7;
const MIN_LOGO_SIZE = 20;
const MAX_LOGO_SIZE = 35;
const MIN_DURATION = 15;
const MAX_DURATION = 30;
const MAX_DELAY = 10;

// --- Helper Function ---
function generateItemParams(minSize, maxSize) {
    const size = minSize + Math.random() * (maxSize - minSize);
    return {
        x: Math.random() * 100, y: Math.random() * 100, size: size,
        opacity: 0.3 + Math.random() * 0.4, // Opacity range
        duration: MIN_DURATION + Math.random() * (MAX_DURATION - MIN_DURATION),
        delay: Math.random() * MAX_DELAY,
        moveX: (Math.random() - 0.5) * 10, moveY: (Math.random() - 0.5) * 15,
        rotate: (Math.random() - 0.5) * 30,
    };
}

// --- Main Component ---
const FloatingItemsBackground = () => {
    const [dots, setDots] = useState([]);
    const [logos, setLogos] = useState([]);

    useEffect(() => {
        // Generate dots
        const generatedDots = [];
        for (let i = 0; i < NUM_DOTS; i++) { generatedDots.push({ id: `dot-${i}`, ...generateItemParams(MIN_DOT_SIZE, MAX_DOT_SIZE) }); }
        setDots(generatedDots);
        // Generate logos
        const generatedLogos = [];
        for (let i = 0; i < NUM_LOGOS; i++) { generatedLogos.push({ id: `logo-${i}`, ...generateItemParams(MIN_LOGO_SIZE, MAX_LOGO_SIZE) }); }
        setLogos(generatedLogos);
    }, []);

    return (
        <motion.div
            className={`inset-0 -z-1 overflow-hidden pointer-events-none bg-gradient-to-br ${colors.gradientFrom} ${colors.gradientTo}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            aria-hidden="true"
        >
            {/* Render Dots */}
            {dots.map(dot => (
                <motion.div
                    key={dot.id}
                    className="absolute rounded-full" // Dots are simple divs
                    style={{
                        left: `${dot.x}%`, top: `${dot.y}%`,
                        width: `${dot.size}px`, height: `${dot.size}px`,
                        opacity: dot.opacity, backgroundColor: colors.dotColor,
                    }}
                    animate={{
                        x: [`0%`, `${dot.moveX}%`, `0%`],
                        y: [`0%`, `${dot.moveY}%`, `0%`],
                        scale: [1, 1.1, 1],
                    }}
                    transition={{
                        duration: dot.duration, delay: dot.delay,
                        repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut',
                    }}
                />
            ))}

            {/* Render Logos */}
            {logos.map(logo => (
                <motion.div
                    key={logo.id}
                    // Removed placeholder background class, added filter for potential slight glow
                    className={`absolute rounded flex items-center justify-center filter drop-shadow-sm`}
                    style={{
                        left: `${logo.x}%`, top: `${logo.y}%`,
                        width: `${logo.size}px`, height: `${logo.size}px`,
                        opacity: logo.opacity, // Control opacity via the container
                    }}
                    animate={{
                        x: [`0%`, `${logo.moveX}%`, `0%`],
                        y: [`0%`, `${logo.moveY}%`, `0%`],
                        rotate: [0, logo.rotate, 0],
                        scale: [1, 1.05, 1],
                    }}
                    transition={{
                        duration: logo.duration * 1.2, delay: logo.delay,
                        repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut',
                    }}
                >
                    {/* *** UPDATED SVG USAGE *** */}
                    {/* Use an img tag with the imported URL */}
                    <img
                       src={anacoLogoUrl} // Use the imported URL path
                       alt="" // Alt text is empty as it's decorative
                       className="w-full h-full object-contain" // Fill container, maintain aspect ratio
                    />
                    {/* *** END UPDATED SVG USAGE *** */}

                </motion.div>
            ))}
        </motion.div>
    );
};

FloatingItemsBackground.propTypes = {
  // Props definition
};
export default FloatingItemsBackground;
